import { Link } from '@tanstack/react-router'
import { useState } from 'react'
import { ApiError } from '../api/client'
import {
  useAddExistingMember,
  useAddManagedMember,
  useRemoveMember,
  useSetMemberRole,
} from '../api/hooks'
import type { GroupMemberWithBlock } from '../api/types'
import { MeepleAvatar, Pill, ScorePad, ScorePadRow } from '../primitives'

export const MembersTab = ({
  groupId,
  members,
  loading,
  viewerId,
  viewerIsAdmin,
}: {
  groupId: string
  members: GroupMemberWithBlock[] | undefined
  loading: boolean
  viewerId: string | undefined
  viewerIsAdmin: boolean
}) => {
  const setRole = useSetMemberRole(groupId)
  const remove = useRemoveMember(groupId)
  const [actionError, setActionError] = useState<string | null>(null)
  const [pendingRemoveId, setPendingRemoveId] = useState<string | null>(null)

  if (loading) return <div style={{ color: 'var(--ink-3)' }}>Loading…</div>
  if (!members?.length) return null

  const onRoleToggle = async (member: GroupMemberWithBlock) => {
    setActionError(null)
    try {
      await setRole.mutateAsync({
        userId: member.userId,
        role: member.role === 'admin' ? 'member' : 'admin',
      })
    } catch (err) {
      setActionError(err instanceof ApiError ? err.friendlyMessage : 'Could not change role')
    }
  }

  const onRemoveConfirm = async (member: GroupMemberWithBlock) => {
    setActionError(null)
    try {
      await remove.mutateAsync(member.userId)
      setPendingRemoveId(null)
    } catch (err) {
      setActionError(err instanceof ApiError ? err.friendlyMessage : 'Could not remove')
      setPendingRemoveId(null)
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {viewerIsAdmin && <AddMemberPanel groupId={groupId} />}

      <ScorePad>
        {members.map((m) => {
          const isSelf = m.userId === viewerId
          const showAdminControls = viewerIsAdmin && !isSelf
          return (
            <ScorePadRow key={m.userId} style={{ alignItems: 'flex-start', padding: '12px 14px' }}>
              <MeepleAvatar
                name={m.displayName}
                hue={m.avatarHue}
                shape={m.avatarShape}
                size={36}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <Link
                    to="/u/$handle"
                    params={{ handle: m.handle }}
                    style={{
                      fontWeight: 600,
                      fontSize: 14,
                      color: 'var(--ink)',
                      textDecoration: 'none',
                    }}
                  >
                    {m.displayName}
                  </Link>
                  {isSelf && <Pill color="terra">you</Pill>}
                  {m.isManaged && <Pill color="slate">managed</Pill>}
                </div>
                <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>
                  @{m.handle}
                  {m.isManaged && (
                    <span style={{ marginLeft: 6, fontStyle: 'italic' }}>· no sign-in yet</span>
                  )}
                </div>
              </div>

              {m.role === 'admin' ? (
                <Pill color="gilt">admin</Pill>
              ) : (
                <Pill color="slate">member</Pill>
              )}

              {showAdminControls && pendingRemoveId !== m.userId && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <button
                    type="button"
                    className="btn-ghost"
                    onClick={() => onRoleToggle(m)}
                    disabled={setRole.isPending}
                    style={{ fontSize: 11, padding: '4px 8px' }}
                  >
                    {m.role === 'admin' ? 'Demote' : 'Promote'}
                  </button>
                  <button
                    type="button"
                    className="btn-ghost"
                    onClick={() => setPendingRemoveId(m.userId)}
                    style={{
                      fontSize: 11,
                      padding: '4px 8px',
                      color: 'var(--terra-ink)',
                      borderColor: 'var(--terra)',
                    }}
                  >
                    Remove
                  </button>
                </div>
              )}

              {showAdminControls && pendingRemoveId === m.userId && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <button
                    type="button"
                    className="btn-stamp"
                    onClick={() => onRemoveConfirm(m)}
                    disabled={remove.isPending}
                    style={{
                      fontSize: 11,
                      padding: '4px 8px',
                      background: 'var(--terra)',
                      borderColor: 'var(--terra)',
                    }}
                  >
                    {remove.isPending ? 'Removing…' : 'Confirm'}
                  </button>
                  <button
                    type="button"
                    className="btn-ghost"
                    onClick={() => setPendingRemoveId(null)}
                    style={{ fontSize: 11, padding: '4px 8px' }}
                  >
                    Cancel
                  </button>
                </div>
              )}
            </ScorePadRow>
          )
        })}
      </ScorePad>

      {actionError && (
        <div
          style={{
            padding: 8,
            fontSize: 12,
            color: 'var(--terra-ink)',
            background: 'var(--terra-bg)',
            borderRadius: 4,
          }}
        >
          {actionError}
        </div>
      )}
    </div>
  )
}

type AddMode = 'existing' | 'managed'

const MODE_OPTIONS: ReadonlyArray<{ id: AddMode; label: string; description: string }> = [
  {
    id: 'existing',
    label: 'Existing user',
    description: 'Adds someone who already has a TopMeeps account by their handle.',
  },
  {
    id: 'managed',
    label: 'New player (no sign-in)',
    description:
      "Creates an account tracked in this group. They accumulate ratings but can't sign in until you ping kwb for a code.",
  },
]

const AddMemberPanel = ({ groupId }: { groupId: string }) => {
  const addExisting = useAddExistingMember(groupId)
  const addManaged = useAddManagedMember(groupId)
  const [open, setOpen] = useState(false)
  const [mode, setMode] = useState<AddMode>('existing')
  const [handle, setHandle] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [error, setError] = useState<string | null>(null)

  const isPending = addExisting.isPending || addManaged.isPending

  const reset = () => {
    setHandle('')
    setDisplayName('')
    setError(null)
    setMode('existing')
  }

  const close = () => {
    reset()
    setOpen(false)
  }

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    const trimmedHandle = handle.trim()
    if (!trimmedHandle) {
      setError('Handle is required')
      return
    }
    if (mode === 'managed' && !displayName.trim()) {
      setError('Display name is required')
      return
    }
    try {
      if (mode === 'existing') {
        await addExisting.mutateAsync(trimmedHandle)
      } else {
        await addManaged.mutateAsync({ handle: trimmedHandle, displayName: displayName.trim() })
      }
      close()
    } catch (err) {
      setError(err instanceof ApiError ? err.friendlyMessage : 'Could not add member')
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        className="btn-ghost"
        onClick={() => setOpen(true)}
        style={{ alignSelf: 'flex-start', fontSize: 12, padding: '6px 10px' }}
      >
        + Add member
      </button>
    )
  }

  return (
    <form
      onSubmit={onSubmit}
      className="felt-card"
      style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}
    >
      <div className="kicker">add member</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {MODE_OPTIONS.map((opt) => (
          <label
            key={opt.id}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10,
              padding: 10,
              border: `1px solid ${mode === opt.id ? 'var(--terra)' : 'var(--line-soft)'}`,
              background: mode === opt.id ? 'var(--terra-bg)' : 'var(--paper)',
              borderRadius: 4,
              cursor: 'pointer',
            }}
          >
            <input
              type="radio"
              name="add-member-mode"
              checked={mode === opt.id}
              onChange={() => {
                setMode(opt.id)
                setError(null)
              }}
              style={{ marginTop: 3 }}
            />
            <div>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{opt.label}</div>
              <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>
                {opt.description}
              </div>
            </div>
          </label>
        ))}
      </div>

      <Field label="Handle">
        <input
          type="text"
          value={handle}
          onChange={(e) => setHandle(e.target.value)}
          placeholder={mode === 'existing' ? 'e.g. jules' : 'lowercase, 3–20 chars'}
          maxLength={20}
          style={inputStyle}
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          autoFocus
        />
      </Field>

      {mode === 'managed' && (
        <Field label="Display name">
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="e.g. Daisy"
            maxLength={50}
            style={inputStyle}
          />
        </Field>
      )}

      {error && <div style={{ fontSize: 12, color: 'var(--terra-ink)' }}>{error}</div>}

      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" className="btn-ghost" onClick={close} style={{ flex: 1 }}>
          Cancel
        </button>
        <button type="submit" className="btn-stamp" disabled={isPending} style={{ flex: 2 }}>
          {isPending ? 'Adding…' : 'Add to group'}
        </button>
      </div>
    </form>
  )
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  background: 'var(--paper)',
  border: '1px solid var(--line-soft)',
  borderRadius: 4,
  fontFamily: 'var(--font-display)',
  fontSize: 14,
  color: 'var(--ink)',
}

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div>
    <div className="kicker" style={{ fontSize: 11 }}>
      {label}
    </div>
    <div style={{ marginTop: 4 }}>{children}</div>
  </div>
)
